import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';
import { Link } from 'react-router';
import { Ellipsis, type LucideIcon } from 'lucide-react';
import { Button, buttonVariants } from '@/components/ui/button';
import { cn } from '@/lib/utils';

export interface PageAction {
  label: string;
  onClick?: () => void;
  /** Renders a router link styled as a button instead of a button. */
  to?: string;
  variant?: 'default' | 'outline' | 'destructive';
  disabled?: boolean;
  /** Leading glyph, for pages whose buttons carried one before. */
  icon?: LucideIcon;
  size?: 'default' | 'sm';
}

interface PageHeaderProps {
  title: ReactNode;
  /** A string renders as the muted line; any other node renders as given. */
  description?: ReactNode;
  /** Falsy entries are dropped, so conditional actions read `cond && { … }`. */
  actions?: (PageAction | false | null | undefined)[];
}

function ActionContent({ action }: { action: PageAction }) {
  const Icon = action.icon;
  return (
    <>
      {Icon && <Icon className="size-4" aria-hidden="true" />}
      {action.label}
    </>
  );
}

function ActionButton({ action }: { action: PageAction }) {
  const variants = { variant: action.variant ?? 'default', size: action.size ?? 'default' } as const;
  if (action.to) {
    return (
      <Link to={action.to} className={cn(buttonVariants(variants))}>
        <ActionContent action={action} />
      </Link>
    );
  }
  return (
    <Button {...variants} onClick={action.onClick} disabled={action.disabled}>
      <ActionContent action={action} />
    </Button>
  );
}

const MENU_ITEM =
  'flex h-11 w-full cursor-pointer items-center gap-2 rounded-[var(--radius-ctl)] px-2.5 text-left text-sm transition-colors hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:cursor-default';

function MenuItem({ action, onSelect }: { action: PageAction; onSelect: () => void }) {
  const className = cn(
    MENU_ITEM,
    action.variant === 'destructive' && 'text-destructive',
    action.disabled && 'opacity-50',
  );
  if (action.to) {
    return (
      <Link to={action.to} role="menuitem" className={className} onClick={onSelect}>
        <ActionContent action={action} />
      </Link>
    );
  }
  return (
    <button
      type="button"
      role="menuitem"
      className={className}
      disabled={action.disabled}
      onClick={() => {
        onSelect();
        action.onClick?.();
      }}
    >
      <ActionContent action={action} />
    </button>
  );
}

/** Phone (< sm): 2+ actions fold into one "More actions" menu. */
function ActionMenu({ actions }: { actions: PageAction[] }) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  const close = useCallback(() => setOpen(false), []);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') close();
    };
    const onPointerDown = (e: PointerEvent) => {
      if (!rootRef.current?.contains(e.target as Node)) close();
    };
    window.addEventListener('keydown', onKey);
    document.addEventListener('pointerdown', onPointerDown);
    return () => {
      window.removeEventListener('keydown', onKey);
      document.removeEventListener('pointerdown', onPointerDown);
    };
  }, [open, close]);

  return (
    <div ref={rootRef} className="relative self-end sm:hidden">
      <button
        type="button"
        aria-label="More actions"
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => setOpen((o) => !o)}
        className={cn(buttonVariants({ variant: 'outline', size: 'icon' }), 'size-11')}
      >
        <Ellipsis className="size-5" aria-hidden="true" />
      </button>
      {open && (
        <div
          role="menu"
          className="absolute right-0 top-full z-30 mt-1 min-w-56 rounded-lg border bg-popover p-1.5 text-popover-foreground shadow-lg"
        >
          {actions.map((action) => (
            <MenuItem key={action.label} action={action} onSelect={close} />
          ))}
        </div>
      )}
    </div>
  );
}

function HeaderActions({ actions }: { actions: PageAction[] }) {
  if (actions.length === 0) return null;
  if (actions.length === 1) {
    return (
      <div className="flex shrink-0 gap-2">
        <ActionButton action={actions[0]!} />
      </div>
    );
  }
  return (
    <>
      <div className="hidden shrink-0 gap-2 sm:flex">
        {actions.map((action) => (
          <ActionButton key={action.label} action={action} />
        ))}
      </div>
      <ActionMenu actions={actions} />
    </>
  );
}

/**
 * Page title + description + actions. Stacks below `sm`; there, two or more
 * actions fold into a "More actions" menu with 44px items.
 */
export function PageHeader({ title, description, actions = [] }: PageHeaderProps) {
  return (
    <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between sm:gap-4">
      <div className="min-w-0">
        <h1 className="break-words text-2xl font-semibold tracking-tight">{title}</h1>
        {typeof description === 'string' ? (
          <p className="text-sm text-muted-foreground">{description}</p>
        ) : (
          description
        )}
      </div>
      <HeaderActions actions={actions.filter((action): action is PageAction => Boolean(action))} />
    </div>
  );
}
